/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Observable, of, timer } from 'rxjs';
import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, filter, first, map, switchMap, tap } from 'rxjs/operators';

// Services
import { ClientService, CompanyService, SuperAdminUserService, UserService } from '@core/services';
import { ApiResponse } from '@models/api-response.model';
// Models
import { CompanyTypesEnum } from '@truckstop-tms/zuum-lib';
import { USER_TYPES } from '@core/models/mongo';
import { ContractAiService } from '@contract-ai/shared/services';
import { ContractAiUserType } from '@contract-ai/shared/enums';
import { ContractAiApiResponse } from '@contract-ai/shared/models';

@Injectable({
  providedIn: 'root',
})
export class ValidatorsService {
  existingDotNumberValidator(
    companyService: CompanyService,
    companyId?: string,
    type?: CompanyTypesEnum | string
  ): AsyncValidatorFn {
    let value = '';
    let response: ValidationErrors | null = null;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value || (value && value === control.value)) {
        return of(response);
      }

      value = control.value;

      return of(control.value).pipe(
        debounceTime(800),
        first(),
        switchMap((dotNumber) =>
          companyService.checkDotNumber({ dotNumber, type: type || control.parent?.get('type')?.value })
        ),
        map((res: ApiResponse) =>
          // if a company with that dotNumber exists, will return success:false
          // if companyId is provided to fn, check to see if the returned company is diff than companyId
          companyId ? (res.result.success ? res.data._id === companyId : !res.result.success) : !res.result.success
        ),
        map((available) => (available ? null : { existingDotNumberValidator: true })),
        tap((result) => (response = result)),
        catchError((error) => of({ existingDotNumberValidator: 'unable to verify state' }))
      );
    };
  }

  /**
   * existingMcNumberValidator
   *
   */
  existingMcNumberValidator(
    companyService: CompanyService,
    companyId?: string,
    type?: CompanyTypesEnum | string
  ): AsyncValidatorFn {
    let value = '';
    let response: ValidationErrors | null = null;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value || (value && value === control.value)) {
        return of(response);
      }

      value = control.value;

      return of(control.value).pipe(
        debounceTime(800),
        first(),
        switchMap((mcNumber) =>
          companyService.checMcNumber({ mcNumber, type: type || control.parent?.get('type')?.value })
        ),
        map((res: ApiResponse) =>
          // if a company with that mcNumber exists, will return success:false
          // if companyId is provided to fn, check to see if the returned company is diff than companyId
          companyId
            ? res.result.success
              ? res.data._id.toString() === companyId.toString()
              : !res.result.success
            : !res.result.success
        ),
        map((available) => (available ? null : { existingMcNumberValidator: true })),
        tap((result) => (response = result)),
        catchError((error) => of({ existingMcNumberValidator: 'unable to verify state' }))
      );
    };
  }

  existingUserPhoneNumber(
    userService: UserService | SuperAdminUserService,
    userId?: string | null,
    tenantId?: string
  ): (control: AbstractControl) => Observable<ValidationErrors | null> {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value || !control.dirty) {
        return of(null);
      }
      return of(control.value).pipe(
        debounceTime(500),
        first(),
        switchMap((phoneNumber) =>
          userService
            .checkPhoneNumber({
              phoneNumber,
              type: control.parent?.get('type')?.value,
              tenantId,
            })
            .pipe(
              map((res: ApiResponse) =>
                // if a user with that phoneNumber exists, will reeturn success:false
                // if userId is provided to fn, check to see if the returned user is diff than userId
                userId
                  ? res.result.success
                    ? res.data._id.toString() === userId.toString()
                    : !res.result.success
                  : !res.result.success
              ),
              map((available) => (available ? null : { existingUserPhoneNumber: true })),
              catchError((error) => {
                return of({
                  existingUserPhoneNumber: 'unable to verify phone number status',
                });
              })
            )
        )
      );
    };
  }

  existingUserEmail(
    userService: UserService | SuperAdminUserService,
    userId?: string | null,
    tenantId?: string,
    checkInitialValue = false
  ): (control: AbstractControl) => Observable<ValidationErrors | null> {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return timer(800).pipe(
        map(() => (control.dirty && control.value !== '') || (checkInitialValue ? control.pristine : false)),
        switchMap((validControlValue) =>
          validControlValue
            ? userService.checkEmail({ email: control.value, type: control.parent?.get('type')?.value, tenantId }).pipe(
                map((res: ApiResponse) =>
                  // if a user with that email exists, will return success:false
                  // if userId is provided to fn, check to see if the returned user is diff than userId
                  userId
                    ? res.result.success
                      ? res.data._id.toString() === userId.toString()
                      : !res.result.success
                    : !res.result.success
                ),
                map((available) => (available ? null : { existingUserEmail: true })),
                catchError((error) => {
                  return of({
                    existingUserEmail: 'unable to verify email status',
                  });
                })
              )
            : of(null)
        )
      );
    };
  }

  existingUserSuperAdminApi(userService: UserService, currentEmail?: string, types?: USER_TYPES[]): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) {
        return of(null);
      }

      if (currentEmail && currentEmail === control.value) {
        // edit user
        return of(null);
      }

      return of(control.value).pipe(
        first(),
        debounceTime(300),
        switchMap((value) => userService.checkEmailSuperAdmin(value, types)),
        map((response) => {
          if (!response.result.success) {
            return null;
          }
          // when data is retuend(users are found)
          return { existing: true };
        }),
        catchError(() => of({ existing: 'Unable to verify' }))
      );
    };
  }

  existingContractAiEmail(
    contractAiService: ContractAiService,
    type: ContractAiUserType,
    currentEmail?: string
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<null | { existing: boolean }> => {
      return timer(500).pipe(
        distinctUntilChanged(),
        map(() => control.dirty && control.value !== ''),
        switchMap((res: boolean) => {
          if (currentEmail === control.value) {
            control.markAsPristine();
            return of(null);
          }
          control.markAsTouched();

          return res
            ? contractAiService
                .existingEmail(control.value, type)
                .pipe(map((response: ContractAiApiResponse<string>) => (!response.data ? { existing: true } : null)))
            : of(null);
        })
      );
    };
  }

  existingBrokerEmail(
    userService: UserService | SuperAdminUserService,
    type: USER_TYPES,
    userId?: string | null
  ): (control: AbstractControl) => Observable<ValidationErrors | null> {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return timer(800).pipe(
        map(() => control.dirty && control.value !== ''),
        switchMap((validControlValue) =>
          validControlValue
            ? userService.checkBrokerEmail({ email: control.value, type }).pipe(
                map((res: ApiResponse) =>
                  // if a user with that email exists, will return success:false
                  // if userId is provided to fn, check to see if the returned user is diff than userId
                  userId
                    ? res.result.success
                      ? res.data._id.toString() === userId.toString()
                      : !res.result.success
                    : !res.result.success
                ),
                map((available) => (available ? null : { existingUserEmail: true })),
                catchError((error) => {
                  return of({
                    existingUserEmail: 'unable to verify email status',
                  });
                })
              )
            : of(null)
        )
      );
    };
  }

  existingRootSubdomainValidator(
    service: ClientService,
    required: boolean,
    excludedTenantId?: string
  ): (control: AbstractControl) => Observable<ValidationErrors | null> {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!required || control.pristine) {
        return of(null);
      }

      return timer(800).pipe(
        map(() => control.dirty && control.value !== ''),
        switchMap((validControlValue) =>
          validControlValue
            ? service.getClientsByRootSubdomain(control.value, excludedTenantId).pipe(
                map(
                  (res: ApiResponse): boolean =>
                    // if a client with this rootSubdomain exists then return false
                    res.result.success && res.data.length === 0
                ),
                map((available) => (available ? null : { existingDomain: true })),
                catchError((error) => {
                  return of({
                    existingDomain: 'unable to verify email status',
                  });
                })
              )
            : of(null)
        )
      );
    };
  }

  requiredCompanyName(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const companyNameRequired = !control?.value?.company || control?.value?.company?.length === 0;
      return companyNameRequired ? { required: { value: true } } : null;
    };
  }

  minLengthValidator(minLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null =>
      control?.value?.company?.length < minLength ? { minLength: { value: true } } : null;
  }
}
